import { useState } from 'react';
import * as yup from 'yup';

import CrudPage from '@modules/cruds/CrudPage';
import { defaultConfigComponent as ItemsConfig } from './Items/Items';
import ItemForm from './ItemForm';

import { buildDefaultValuesFromItemType } from './flavor.utils';

import { Grid, Button, Typography, GridContainerFullHeight } from '@ts/storybook';
import { IElementType, IElement, CElementType } from '@ts/ui-models';

import { useAppDispatch } from '@redux/store';
import { createLocationRules } from './Locations/Location.utils';
import { generateSearchEntitySelector } from '@modules/entitySelector/entitySelector.utils';
import { useRouterContext } from '@utils/context/RouterContext';
import WrapperEntitySelector from '@modules/entitySelector/WrapperEntitySelector';
import { normalizeCEntity } from '@models/entity/Entity.normalizers';
import { getElementTypeById } from '@services/DataBase/ElementType.service';
// ----------------------
// Validation schema
// ----------------------
const itemSchema = yup.object().shape({
  name: yup.string().required().label('Name'),
  customId: yup.string().required().label('Custom ID'),
});

// Type of the defaultValues generated in the pre-creation step
type ItemDefaultValues = ReturnType<typeof buildDefaultValuesFromItemType>;

// ----------------------
// Entry point
// ----------------------

/**
 * Entry component for the Item page.
 * - If `mode === 'new'` → goes through a pre-creation step to choose the ItemType.
 * - Otherwise           → renders a CrudPage bound to the item ID from the route.
 */
const ItemPage = () => {
  const dispatch = useAppDispatch();
  const { transferPropsRoute, mode } = useRouterContext();

  const itemId = transferPropsRoute.params.id as string | undefined;

  /**
   * Extra action executed after a successful submit in edit mode.
   * Used to create location rules based on the `location` flavor.
   */
  const extraActionToDoAfterCorrectSubmit = async (values: IElementType, options: { itemId: string }): Promise<void> => {
    const locationFlavor = values.flavors?.['location'];
    if (!locationFlavor) return;

    if (transferPropsRoute.id) {
      return createLocationRules(dispatch, transferPropsRoute.id, locationFlavor.locationRules, options);
    }
  };

  if (mode === 'new') {
    return <NewItem />;
  }

  return (
    <CrudPage
      key={itemId || 'dummy'}
      configComponent={ItemsConfig}
      form={ItemForm}
      validationSchema={itemSchema}
      extraActionToDoAfterCorrectSubmit={extraActionToDoAfterCorrectSubmit}
      normalize={normalizeCEntity<IElement>}
    />
  );
};

// ----------------------
// New item flow
// ----------------------

const NewItem = () => {
  const { transferPropsRoute } = useRouterContext();

  // Default values generated after selecting an ItemType
  const [itemDefaultValues, setItemDefaultValues] = useState<ItemDefaultValues | null>(null);

  const handleCancel = () => {
    setItemDefaultValues(null);
  };

  // Step 1: choose ItemType and confirm → generates default form values
  if (!itemDefaultValues) {
    return <PreCreation onConfirmItemType={setItemDefaultValues} />;
  }

  // Step 2: render CrudPage with the generated default values
  const itemId = transferPropsRoute.params.id as string | undefined;

  return (
    <CrudPage
      key={itemId || 'dummy'}
      configComponent={ItemsConfig}
      form={ItemForm}
      defaultValues={itemDefaultValues}
      onCancel={handleCancel}
      validationSchema={itemSchema}
      normalize={normalizeCEntity<IElement>}
    />
  );
};

// ----------------------
// Pre-creation: choose ItemType
// ----------------------

interface IPreCreationProps {
  onConfirmItemType: (defaultValues: ItemDefaultValues) => void;
}

/**
 * First step in "new item" flow:
 * - User selects an ItemType.
 * - On confirm, we build the form default values based on that ItemType.
 */
const PreCreation = ({ onConfirmItemType }: IPreCreationProps) => {
  const [itemType, setItemType] = useState<CElementType | null>(null);
  const dispatch = useAppDispatch();

  //#region Handlers
  const handleEntitySelected = async (entity: CElementType | null) => {
    if (!entity) return;
    setItemType(entity);
    //We need this request to get de flavor object
    const elementType = await getElementTypeById(dispatch, entity.getID());
    setItemType(elementType);
  };

  const handleConfirmItemType = () => {
    if (!itemType) return;

    const defaultValues = buildDefaultValuesFromItemType(itemType);
    onConfirmItemType(defaultValues);
  };
  //#endregion

  return (
    <GridContainerFullHeight p={4} id="item-pre-creation">
      <Typography variant="h5" gutterBottom>
        Create a new item
      </Typography>
      <Typography variant="body1" color="text.secondary" mb={3}>
        Start by selecting the item type you want to use as a base.
      </Typography>

      <Grid container size={{ xs: 12, md: 6 }} spacing={3}>
        <Grid size={6}>
          <WrapperEntitySelector
            label="Type of item"
            value={itemType}
            multiple={false}
            onChangeEntity={v => handleEntitySelected(v as CElementType)}
            searchComponent={generateSearchEntitySelector('itemType')}
          />
        </Grid>
        <Grid>
          <Button
            variant="contained"
            color="primary"
            disabled={!itemType}
            fullWidth
            onClick={handleConfirmItemType}
            label="Confirm item type"
          />
        </Grid>
      </Grid>
    </GridContainerFullHeight>
  );
};

export default ItemPage;
